(function () {
  'use strict';

  const productCache = new Map();
  let modal = null;
  let modalState = null;

  function getProductJson(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    let url = `${window.MinimogSettings.routes.root}/products/${handle}.js`;
    url = url.replace('//', '/');
    const promise = fetch(url).then((res) => {
      if (!res.ok) throw new Error(`Failed to load product: ${handle}`);
      return res.json();
    });
    productCache.set(handle, promise);
    return promise;
  }

  // Option positions (1-based) already selectable via swatches on the card.
  function getCardSelectedOptions(card) {
    const selected = {};
    const covered = new Set();
    card.querySelectorAll('[data-pcard-variant-picker] .m-product-option--node__label').forEach((label) => {
      const position = Number(label.dataset.optionPosition);
      if (!position) return;
      covered.add(position);
      if (label.dataset.selected === 'true') selected[position] = label.dataset.value;
    });
    return { selected, covered };
  }

  function findVariant(product, optionValues) {
    return (
      product.variants.find((variant) =>
        product.options.every((_, index) => {
          const value = optionValues[index + 1];
          return value == null || variant.options[index] === value;
        })
      ) || null
    );
  }

  function addToCart(variantId, button) {
    const cart = document.querySelector('m-cart-drawer');
    const config = {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    };
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
    }
    config.body = body;

    button && button.classList.add('m-spinner-loading');

    const { MinimogSettings, MinimogStrings } = window;
    return fetch(MinimogSettings.routes.cart_add_url, config)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartError, {
            source: 'pfc-card-popup',
            productVariantId: variantId,
            errors: data.description,
            message: data.message,
          });
          window.MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: data.message,
            last: 3000,
            sticky: false,
          });
          return data;
        }
        if (MinimogSettings.use_ajax_atc && cart && MinimogSettings.enable_cart_drawer) {
          cart.renderContents(data);
          MinimogTheme.Notification.show({
            target: cart.querySelector('m-cart-drawer-items'),
            method: 'prepend',
            type: 'success',
            message: MinimogStrings.itemAdded,
            delay: 400,
          });
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
        } else {
          window.location = MinimogSettings.routes.cart;
        }
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, data);
        return data;
      })
      .finally(() => {
        button && button.classList.remove('m-spinner-loading');
        if (cart) {
          cart.classList.remove('m-cart--empty');
          const items = cart.querySelector('m-cart-drawer-items');
          items && items.classList.remove('m-cart--empty');
        }
      });
  }

  /* ---------------- Modal ---------------- */

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'pfc-option-modal';
    modal.setAttribute('hidden', '');
    modal.innerHTML = `
      <div class="pfc-option-modal__overlay" data-pfc-modal-close></div>
      <div class="pfc-option-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="pfc-option-modal-title">
        <button type="button" class="pfc-option-modal__close" data-pfc-modal-close aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <h3 class="pfc-option-modal__title" id="pfc-option-modal-title"></h3>
        <div class="pfc-option-modal__fields"></div>
        <div class="pfc-option-modal__actions">
          <button type="button" class="pfc-btn pfc-btn--primary pfc-btn--lg pfc-btn--block pfc-option-modal__atc m-spinner-button" disabled>
            <span class="m-spinner-icon">
              <svg class="animate-spin m-svg-icon--medium" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span data-pfc-atc-text>Add to Cart</span>
          </button>
          <a class="pfc-btn pfc-btn--outline pfc-btn--lg pfc-btn--block pfc-option-modal__details" href="#">See full details</a>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-pfc-modal-close]')) closeModal();
    });
    modal.querySelector('.pfc-option-modal__fields').addEventListener('change', updateModalState);
    modal.querySelector('.pfc-option-modal__atc').addEventListener('click', onModalAtc);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });
    return modal;
  }

  function openModal(product, lockedValues, remainingPositions, productUrl) {
    ensureModal();
    modalState = { product, lockedValues, remainingPositions };

    const title =
      remainingPositions.length === 1
        ? `Select a ${product.options[remainingPositions[0] - 1]}`
        : 'Select Options';
    modal.querySelector('.pfc-option-modal__title').textContent = title;
    modal.querySelector('.pfc-option-modal__details').href = productUrl;

    // Variants matching the options already locked in on the card (e.g. color).
    const candidates = product.variants.filter((variant) =>
      Object.entries(lockedValues).every(([position, value]) => variant.options[position - 1] === value)
    );

    const fields = modal.querySelector('.pfc-option-modal__fields');
    fields.innerHTML = '';
    const selectedSoFar = Object.assign({}, lockedValues);
    remainingPositions.forEach((position) => {
      const name = product.options[position - 1];
      const values = [];
      candidates.forEach((variant) => {
        const value = variant.options[position - 1];
        if (!values.includes(value)) values.push(value);
      });
      const select = document.createElement('select');
      select.className = 'pfc-option-modal__select';
      select.dataset.optionPosition = position;
      select.setAttribute('aria-label', name);
      const preferred =
        values.find((value) => {
          const optionValues = Object.assign({}, selectedSoFar, { [position]: value });
          const variant = findVariant(product, optionValues);
          return variant && variant.available;
        }) || values[0];
      if (preferred) selectedSoFar[position] = preferred;
      select.innerHTML = values
        .map((value) => {
          const selected = value === preferred ? ' selected' : '';
          return `<option value="${value.replace(/"/g, '&quot;')}"${selected}>${value}</option>`;
        })
        .join('');
      fields.appendChild(select);
    });

    updateModalState();
    modal.removeAttribute('hidden');
    requestAnimationFrame(() => modal.classList.add('pfc-option-modal--open'));
    document.documentElement.classList.add('prevent-scroll');
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('pfc-option-modal--open');
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove('prevent-scroll');
    modalState = null;
  }

  function resolveModalVariant() {
    if (!modalState) return { complete: false, variant: null };
    const values = Object.assign({}, modalState.lockedValues);
    let complete = true;
    modal.querySelectorAll('.pfc-option-modal__select').forEach((select) => {
      if (!select.value) complete = false;
      else values[Number(select.dataset.optionPosition)] = select.value;
    });
    if (!complete) return { complete: false, variant: null };
    return { complete: true, variant: findVariant(modalState.product, values) };
  }

  function updateModalState() {
    if (!modalState) return;
    const button = modal.querySelector('.pfc-option-modal__atc');
    const text = modal.querySelector('[data-pfc-atc-text]');
    const { complete, variant } = resolveModalVariant();
    if (!complete) {
      button.disabled = true;
      text.textContent = 'Add to Cart';
    } else if (!variant) {
      button.disabled = true;
      text.textContent = window.MinimogStrings ? MinimogStrings.unavailable : 'Unavailable';
    } else if (!variant.available) {
      button.disabled = true;
      text.textContent = window.MinimogStrings ? MinimogStrings.soldOut : 'Sold out';
    } else {
      button.disabled = false;
      text.textContent = 'Add to Cart';
    }
  }

  function onModalAtc() {
    const { complete, variant } = resolveModalVariant();
    if (!complete || !variant || !variant.available) return;
    const button = modal.querySelector('.pfc-option-modal__atc');
    addToCart(variant.id, button).then((data) => {
      if (!data || !data.status) closeModal();
    });
  }

  /* ---------------- Card button ---------------- */

  function onCardAtcClick(event, button) {
    event.preventDefault();
    const card = button.closest('.m-product-card');
    const handle = button.dataset.productHandle;
    const productUrl = button.dataset.productUrl;
    if (!handle) return;

    button.classList.add('m-spinner-loading');
    getProductJson(handle)
      .then((product) => {
        button.classList.remove('m-spinner-loading');
        const { selected, covered } = card
          ? getCardSelectedOptions(card)
          : { selected: {}, covered: new Set() };

        const remaining = [];
        product.options.forEach((_, index) => {
          if (!covered.has(index + 1)) remaining.push(index + 1);
        });

        if (remaining.length === 0) {
          const idInput = card && card.querySelector('[name="id"]');
          let variant = null;
          if (idInput && idInput.value) {
            variant = product.variants.find((v) => v.id === Number(idInput.value)) || null;
          }
          if (!variant) variant = findVariant(product, selected);
          if (variant && variant.available) {
            addToCart(variant.id, button);
          } else if (productUrl) {
            window.location = productUrl;
          }
          return;
        }

        openModal(product, selected, remaining, productUrl || product.url);
      })
      .catch(() => {
        button.classList.remove('m-spinner-loading');
        if (productUrl) window.location = productUrl;
      });
  }

  function init() {
    addEventDelegate({
      selector: '.pfc-card-atc',
      handler: onCardAtcClick,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
